'use client';

import { useState, useEffect, useRef } from 'react';
import type { ArticleItem } from '@/lib/contentful/types';
import ArticleCard from '@/components/ui/ArticleCard';
import BlogFilters from './BlogFilters';
import BlogPagination from './BlogPagination';
import { useArticleFiltering } from './useArticleFiltering';
import { COPY } from '@/lib/copy';

const PER_PAGE = 12;

type Props = {
  articles: ArticleItem[];
  featuredArticles: ArticleItem[];
};

export default function BlogAllPosts({ articles, featuredArticles }: Props) {
  const {
    all,
    filtered,
    tags,
    tagCounts,
    activeTags,
    search,
    handleTagToggle,
    handleClearTags,
    handleSearchChange,
  } = useArticleFiltering(articles, featuredArticles);

  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const pageItems = filtered.slice(
    (safePage - 1) * PER_PAGE,
    safePage * PER_PAGE,
  );

  const gridRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
    gridRef.current?.focus({ preventScroll: true });
  }, [safePage]);

  function onTagToggle(tag: string) {
    handleTagToggle(tag);
    setCurrentPage(1);
  }

  function onClearTags() {
    handleClearTags();
    setCurrentPage(1);
  }

  function onSearchChange(value: string) {
    handleSearchChange(value);
    setCurrentPage(1);
  }

  if (all.length === 0) {
    return (
      <div className="px-[6vw] py-16">
        <div className="max-w-[1024px] mx-auto">
          <p className="font-serif italic text-[1.1rem] text-[var(--text-mid)]">
            {COPY.blog.listing.empty}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <BlogFilters
        tags={tags}
        tagCounts={tagCounts}
        activeTags={activeTags}
        allCount={all.length}
        search={search}
        onTagToggle={onTagToggle}
        onClearTags={onClearTags}
        onSearchChange={onSearchChange}
        filteredCount={filtered.length}
      />

      <div className="px-[6vw] pt-12 pb-16 bg-bg-main">
        <div className="max-w-[1024px] mx-auto">
          <h2 className="sr-only">{COPY.blog.listing.heading}</h2>
          {filtered.length === 0 ? (
            <p
              role="status"
              aria-live="polite"
              aria-atomic="true"
              className="font-serif italic text-[1.1rem] text-[var(--text-mid)] py-16 text-center col-span-3"
            >
              {COPY.blog.listing.noResults}
            </p>
          ) : (
            <div
              ref={gridRef}
              tabIndex={-1}
              className="grid grid-cols-1 tablet:grid-cols-2 desktop:grid-cols-3 gap-6 focus:outline-none"
            >
              {pageItems.map((article, i) => (
                <ArticleCard key={`${article.slug}-${i}`} article={article} />
              ))}
            </div>
          )}
        </div>
      </div>

      <BlogPagination
        currentPage={safePage}
        totalPages={totalPages}
        onGoToPage={setCurrentPage}
      />
    </>
  );
}
